/* tslint:disable */
/* eslint-disable */
/**
 * DigitalOrderForm
 * DigitalOrderForm backend API
 *
 * Client for the sap code quota endpoints.
 *
 * Kept in its own module instead of inside the generated api.ts, so that a future regeneration of that file
 * does not wipe it. Shape and conventions follow the generated client: same BaseAPI, same Configuration,
 * baseOptions carry the bearer token set up by the api provider.
 */

import { AxiosPromise } from 'axios';

import { TypedApi } from './typed-api-base';

export interface SapCodeQuotaDto {
	id: number;
	sapCode: string;
	responsabilityAreaCode: string;
	responsabilityAreaName?: string | null;
	maxQuantity: number;
	usedQuantity: number;
	remainingQuantity: number;
	active: boolean;
	note?: string | null;
	lastResetAt?: string | null;
	lastResetBy?: string | null;
}

/** Payload accepted on create and update. The computed fields of SapCodeQuotaDto are ignored by the backend. */
export interface SapCodeQuotaWriteDto {
	sapCode: string;
	responsabilityAreaCode: string;
	maxQuantity: number;
	active: boolean;
	note?: string | null;
}

export interface SapCodeQuotaSearchResultDto {
	items: SapCodeQuotaDto[];
	totalCount: number;
}

export interface SapCodeQuotaSaveResultDto {
	quota: SapCodeQuotaDto;
	error?: string | null;
	/** Set when the configured quota is below the quantity already ordered. The save went through anyway. */
	warning?: string | null;
	isValid: boolean;
}

export interface SapCodeQuotaAvailabilityDto {
	sapCode: string;
	maxQuantity: number;
	usedQuantity: number;
	remainingQuantity: number;
	/** Quantity sitting in other open carts of the same area. It does not consume quota, it is only a warning. */
	pendingInOtherDraftsQuantity: number;
}

/** One sap code whose quota is not enough for what the order asks. */
export interface OrderQuotaErrorDto {
	sapCode: string;
	responsabilityAreaCode?: string | null;
	requestedQuantity: number;
	maxQuantity: number;
	remainingQuantity: number;
}

/** One limited edition number the order can no longer use, or a row missing one. */
export interface LimitedEditionConflictDto {
	sapCode?: string | null;
	/** Null when the problem is a row that carries no number at all. */
	number?: string | null;
	reason: string;
}

/**
 * Body of the 409 Conflict an order answers with when it cannot go through as it is, both when saving the
 * cart and when confirming it. One shape whatever the reason, so the client can say which of the two
 * happened instead of guessing from the fields.
 */
export interface OrderConflictDto {
	quotaErrors?: OrderQuotaErrorDto[] | null;
	limitedEditionErrors?: LimitedEditionConflictDto[] | null;
}

export interface SapCodeQuotaImportErrorDto {
	/** Row number as shown in Excel, so the user can jump straight to it. */
	rowNumber: number;
	sapCode?: string | null;
	responsabilityAreaCode?: string | null;
	reason: string;
}

export interface SapCodeQuotaImportResultDto {
	/** Rows found in the file, header excluded. */
	totalRows: number;
	created: number;
	updated: number;
	skipped: number;
	errors: SapCodeQuotaImportErrorDto[];
}

/** A responsability area, for the administration dropdown. */
export interface SapCodeQuotaAreaDto {
	code: string;
	name?: string | null;
}

export interface SapCodeQuotaSearchParams {
	responsabilityAreaCode?: string;
	sapCode?: string;
	skip?: number;
	take?: number;
}

export class QuotaApi extends TypedApi {
	/**
	 * @summary Search quota rows (admin)
	 * @description Consumed and remaining quantity come already computed by the backend.
	 */
	public searchSapCodeQuotas(
		params: SapCodeQuotaSearchParams = {},
		options?: any,
	): AxiosPromise<SapCodeQuotaSearchResultDto> {
		return this.request('GET', '/admin/quotas', options, {
			query: {
				responsabilityAreaCode: params.responsabilityAreaCode,
				sapCode: params.sapCode,
				skip: params.skip,
				take: params.take,
			},
		});
	}

	/**
	 * @summary Create a quota row (admin)
	 * @description Answers 400 when area or sap code do not exist, when the quantity is negative, or when a
	 * quota already exists for the same pair.
	 */
	public createSapCodeQuota(
		quota: SapCodeQuotaWriteDto,
		options?: any,
	): AxiosPromise<SapCodeQuotaSaveResultDto> {
		return this.request('POST', '/admin/quotas', options, { data: quota });
	}

	/**
	 * @summary Update a quota row (admin)
	 * @description Lowering the maximum below the consumed quantity succeeds and comes back with a warning.
	 */
	public updateSapCodeQuota(
		id: number,
		quota: SapCodeQuotaWriteDto,
		options?: any,
	): AxiosPromise<SapCodeQuotaSaveResultDto> {
		return this.request('PUT', `/admin/quotas/${encodeURIComponent(id)}`, options, {
			data: quota,
		});
	}

	/**
	 * @summary Delete a quota row (admin)
	 * @description The sap code goes back to being unlimited for that area.
	 */
	public deleteSapCodeQuota(id: number, options?: any): AxiosPromise<void> {
		return this.request('DELETE', `/admin/quotas/${encodeURIComponent(id)}`, options);
	}

	/**
	 * @summary Reset the counter of a quota row (admin)
	 * @description Orders confirmed before the reset stop being counted. Author and date are tracked.
	 */
	public resetSapCodeQuotaCounter(
		id: number,
		options?: any,
	): AxiosPromise<SapCodeQuotaSaveResultDto> {
		return this.request(
			'POST',
			`/admin/quotas/${encodeURIComponent(id)}/reset`,
			options,
		);
	}

	/**
	 * @summary Quota availability for a customer
	 * @description One entry per sap code under quota for the responsability area of the customer. An empty
	 * list means no sap code is limited. Used by the product detail page and by the cart.
	 * @param excludeOrderId Order to leave out of the draft quantities, typically the cart being edited.
	 */
	public getQuotaAvailability(
		customerId: number,
		excludeOrderId?: number,
		options?: any,
	): AxiosPromise<SapCodeQuotaAvailabilityDto[]> {
		return this.request('GET', '/quotas/availability', options, {
			query: { customerId, excludeOrderId },
		});
	}

	/**
	 * @summary List the responsability areas a quota can point to (admin)
	 * @description Comes from the ResponsabilityArea table, not from the limitation values, which prefix their
	 * values because there the value is part of a composite key with the sales channel.
	 */
	public getQuotaAreas(options?: any): AxiosPromise<SapCodeQuotaAreaDto[]> {
		return this.request('GET', '/admin/quotas/areas', options);
	}

	/**
	 * @summary Import quotas from an Excel file (admin)
	 * @description Upsert: valid rows are created or updated, quotas missing from the file are left alone.
	 * Invalid rows come back in the errors list with their row number and reason.
	 */
	public importSapCodeQuotas(
		file: File | Blob,
		options?: any,
	): AxiosPromise<SapCodeQuotaImportResultDto> {
		const formData = new FormData();
		formData.append('file', file);

		return this.request('POST', '/admin/quotas/import', options, {
			data: formData,
			contentType: 'multipart/form-data',
		});
	}

	/**
	 * @summary Export quotas to Excel (admin)
	 * @description Configuration columns plus consumed, remaining and last reset. The calculated columns are
	 * ignored if the file is imported back.
	 */
	public exportSapCodeQuotas(options?: any): AxiosPromise<Blob> {
		return this.request('GET', '/admin/quotas/export', {
			responseType: 'blob',
			...options,
		});
	}

	/**
	 * @summary Download the quota import template (admin)
	 */
	public exportSapCodeQuotasImportTemplate(options?: any): AxiosPromise<Blob> {
		return this.request('GET', '/admin/quotas/template', {
			responseType: 'blob',
			...options,
		});
	}
}
